import { DestroyRef, Injectable, Signal, effect, inject, signal } from '@angular/core';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { ApiResponse } from '../../../types/commons';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import { ConversionEventService } from '../common/conversion-event.service';
import { HttpStatus } from '../../utils/http-status.util';
import { normalizeApiErrors } from '../../utils/api-errors.util';
import { resolveFsErrorMessage } from '../../utils/fs-validation-errors.util';
import { formatFileSize } from '../../utils/file-size.util';
import { FilingSessionLimitsService } from './filing-session-limits.service';
import { FilingSessionStore } from './filing-session-store.service';
import { FormSubmitService } from './form-submit.service';

export type UploadResultEvent =
  | { kind: 'success'; fsFileName: string }
  | { kind: 'error'; message: string };

/**
 * Drives the 3-step filing-session upload flow (create → upload files → submit)
 * from a root-provided service so it survives component destruction — e.g. when
 * the user navigates away from the dashboard mid-upload on a slow connection.
 *
 * The component retains the FormControl<File[]> for pre-submit validation and
 * passes the file array into submit() by reference; this service holds that
 * reference for the duration of the run so the async loop completes even if
 * the caller unmounts.
 *
 * Backend constraint — the session expiry is a **rolling 10-minute deadline
 * stamped on the row, NOT a lifecycle budget measured from creation**
 * (confirmed against `FilingSessionService` on 2026-08-14; an earlier comment
 * here described it as a total-lifecycle TTL, which was wrong). Two things
 * follow, and both are load-bearing:
 *
 *   - `POST .../files` does NOT refresh it. Uploading nine files over nine
 *     minutes leaves one minute to submit.
 *   - `DELETE .../files` DOES refresh it, to a full fresh 10 minutes from the
 *     moment of the call.
 *
 * That second point is why `markSelectionChanged` defers its reconcile to
 * submit time: it renews the window immediately before the upload burst, when
 * the user needs it, rather than before a file swap they may spend minutes on.
 *
 * A session past its deadline is soft-deleted by a server-side sweeper within
 * ~60 seconds, which is why a stale id can answer either 410 or 404 — see
 * `resolveSession`.
 */
@Injectable({ providedIn: 'root' })
export class FilingSessionUploader {
  private readonly formSubmitService = inject(FormSubmitService);
  private readonly sessionStore = inject(FilingSessionStore);
  private readonly conversionEvent = inject(ConversionEventService);
  private readonly authService = inject(AuthService);
  private readonly limitsService = inject(FilingSessionLimitsService);

  /**
   * Set by `markSelectionChanged()`, consumed and cleared by `resolveSession()`.
   *
   * Deliberately a plain field rather than a signal: nothing renders from it,
   * and it is read exactly once per submit run.
   *
   * In-memory only, unlike the session id itself (which `FilingSessionStore`
   * persists to sessionStorage). That is correct — the selection it describes
   * is a `File[]` held in component memory, which a reload destroys anyway, so
   * a flag surviving the reload would describe a selection that no longer
   * exists.
   */
  private selectionChangedSinceUpload = false;

  private readonly isSubmittingSignal = signal<boolean>(false);
  readonly isSubmitting: Signal<boolean> = this.isSubmittingSignal.asReadonly();

  private readonly resultSubject = new Subject<UploadResultEvent>();
  /**
   * Emits success/failure on every submit() run so a persistent host
   * (MainLayoutComponent) can render a toast even if the dashboard upload
   * widget has already unmounted.
   *
   * DEVELOPER NOTE (toast-key collision): MainLayoutComponent forwards these
   * events to MessageService with key 'form'. The SignalR listeners in
   * main-layout.component.ts:82-101 ALSO add toasts with key 'form' on
   * conversion-progress events. Back-to-back 'form' toasts can stack when a
   * successful upload is followed shortly by a conversion-progress ping from
   * the backend — accepted trade-off. Re-keying this stream requires
   * re-keying the SignalR listeners and the <p-toast> host in
   * main-layout.component.html:163 together.
   */
  readonly result$: Observable<UploadResultEvent> = this.resultSubject.asObservable();

  constructor() {
    const destroyRef = inject(DestroyRef);
    // Warn via native browser prompt if the user tries to close/refresh the
    // tab while an upload is in flight. The in-memory File[] cannot survive a
    // reload, so bailing now loses their selection. Owned by this root service
    // so the handler persists across component lifecycles.
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (this.isSubmittingSignal()) {
        event.preventDefault();
        // Required for legacy Chromium/Edge to trigger the prompt.
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    destroyRef.onDestroy(() =>
      window.removeEventListener('beforeunload', beforeUnload)
    );

    // Clear upload state on logout so the next login starts fresh and does not
    // inherit a stale sessionId from sessionStorage.
    //
    // `AuthService.clearAuthState()` now clears `FilingSessionStore` directly
    // too, so the id itself is covered twice over — deliberately. This effect
    // remains the only thing that resets `isSubmitting` and
    // `selectionChangedSinceUpload`, which are private to this service and which
    // AuthService has no business knowing about, so it is NOT redundant. Do not
    // delete it on the assumption that the auth-side wipe covers everything.
    effect(() => {
      if (this.authService.isAuthenticated() === false) {
        this.reset();
      }
    });
  }

  /**
   * Records that the user changed their file selection, so the next submit
   * reconciles the server-side session before uploading. Call from every
   * add/remove path.
   *
   * WHY NOT JUST DROP THE SESSION: this used to be `abandonStaleSession()`,
   * which cleared the stored id outright so the next submit created a fresh
   * session. That solved the real problem — a resumed session still holding
   * files the user has since removed would submit a file they did not intend —
   * but it stranded the previous session, orphaned any files already inside it,
   * and left an abandoned Conversion row behind.
   *
   * That was survivable while a selection change was a rare user whim. The
   * backend's financial-statement content guard made it the norm: the only
   * recovery from a rejected upload is to pick a different file, so every
   * rejected user hit the orphaning path. The session must survive a rejection.
   *
   * The reconcile itself is deferred to `resolveSession` rather than fired
   * here, for two reasons: it costs one request instead of one per file the
   * user adds (and avoids racing a burst of adds), and `DELETE .../files`
   * refreshes the session's expiry window — which is worth far more spent
   * immediately before the upload burst than at the start of a file swap the
   * user may spend minutes on.
   *
   * @see `resolveSession` — where the flag is consumed.
   * @see `filing-session-uploader.fs-validation.spec.ts`, "session reuse across
   *      a selection change" — pins every branch of this behaviour.
   */
  markSelectionChanged(): void {
    this.selectionChangedSinceUpload = true;
  }

  /**
   * Runs the 3-step filing-session upload flow:
   *   1) resolveSession (resume existing or create new)
   *   2) upload each file not yet on the server
   *   3) submit / finalize
   *
   * Returns `true` on success, `false` on any handled error. The loop holds
   * its own local copy of `files` so the upload continues to completion even
   * if the caller component is destroyed mid-run.
   *
   * `engine` is the optional conversion-engine override chosen at the
   * dashboard picker, forwarded verbatim to `submitFilingSession`. Undefined or
   * null means "use the server's configured default".
   */
  async submit(
    files: readonly File[],
    fsFileName: string,
    engine?: string | null,
  ): Promise<boolean> {
    if (this.isSubmittingSignal()) return false;

    this.isSubmittingSignal.set(true);
    const queue = [...files];

    try {
      const resolved = await this.resolveSession(fsFileName);
      if (!resolved) return false;

      const { sessionId, alreadyUploaded } = resolved;

      for (const file of queue) {
        if (alreadyUploaded.has(file.name)) continue;

        const uploadRes = await firstValueFrom(
          this.formSubmitService.uploadSessionFile(sessionId, file)
        );

        if (!uploadRes.status) {
          this.emitUploadError(uploadRes, file);
          return false;
        }
      }

      const submitRes = await firstValueFrom(
        this.formSubmitService.submitFilingSession(sessionId, engine)
      );

      if (!submitRes.status) {
        this.emitError(submitRes, fsFileName);
        return false;
      }

      this.sessionStore.clear();
      this.resultSubject.next({ kind: 'success', fsFileName });
      this.conversionEvent.triggerRefetch();
      return true;
    } catch (err) {
      console.error('Unexpected filing session error:', err);
      this.resultSubject.next({
        kind: 'error',
        message: 'An unexpected error occurred while submitting. Please try again later.',
      });
      return false;
    } finally {
      this.isSubmittingSignal.set(false);
    }
  }

  private reset(): void {
    this.isSubmittingSignal.set(false);
    this.sessionStore.clear();
    this.selectionChangedSinceUpload = false;
  }

  private async resolveSession(
    fsFileName: string
  ): Promise<{ sessionId: string; alreadyUploaded: Set<string> } | null> {
    const existingId = this.sessionStore.get();

    if (existingId) {
      if (this.selectionChangedSinceUpload) {
        // The server's file list describes a selection the user has since
        // changed, so it must not be consulted — clear it instead. This keeps
        // the session (and refreshes its expiry) rather than orphaning it.
        const cleared = await firstValueFrom(
          this.formSubmitService.deleteSessionFiles(existingId)
        );
        // `!cleared` covers a 204 No Content, which HttpClient surfaces as a
        // null body rather than an ApiResponse envelope. Any non-2xx would have
        // been converted into a `{ status:false }` envelope by
        // `createErrorHandler` first, so a null body here can only mean success.
        if (!cleared || cleared.status) {
          this.selectionChangedSinceUpload = false;
          return { sessionId: existingId, alreadyUploaded: new Set<string>() };
        }

        // A 400 here does NOT mean expiry. It means the session exists and is
        // live, but has already left `AwaitingFiles` — it was submitted and
        // Phase 1 is underway. Creating a fresh session would start a SECOND
        // conversion for work already running, so surface it instead of
        // silently recovering. The stored id is dropped because this session
        // can never accept files again; keeping it would wedge every future
        // attempt on the same error, and it survives reloads in sessionStorage.
        if (cleared.statusCode === HttpStatus.BadRequest) {
          this.sessionStore.clear();
          this.selectionChangedSinceUpload = false;
          this.emitError(cleared, fsFileName);
          return null;
        }

        // Expired or gone mid-swap. Both 410 and 404 mean the same thing here,
        // and which one arrives is a race with a 60-second server-side sweeper:
        // expired under ~60s ago answers 410 Gone, and once the sweeper has
        // soft-deleted the row the same call answers 404 Not Found. Either way
        // the reconcile is moot, so fall through to a fresh session — the
        // user's File[] is still in memory, so every file simply re-uploads and
        // the run continues transparently.
        this.sessionStore.clear();
      } else {
        const listRes = await firstValueFrom(
          this.formSubmitService.listSessionFiles(existingId)
        );
        if (listRes.status) {
          return {
            sessionId: existingId,
            alreadyUploaded: new Set(listRes.data.map((file) => file.fileName)),
          };
        }
        // Session expired or gone — discard and fall through to create fresh.
        this.sessionStore.clear();
      }
    }

    const sessionRes = await firstValueFrom(
      this.formSubmitService.createFilingSession(fsFileName)
    );

    if (!sessionRes.status) {
      this.emitError(sessionRes, fsFileName);
      return null;
    }

    const newId = sessionRes.data.sessionId;
    this.sessionStore.set(newId);
    // A brand-new session holds no files, so there is nothing left to reconcile.
    this.selectionChangedSinceUpload = false;
    return { sessionId: newId, alreadyUploaded: new Set() };
  }

  /**
   * Error path for a per-file upload. A 413 gets purpose-built copy composed
   * from the server's real cap and the LOCAL `file.size`; everything else falls
   * through to the generic handler.
   *
   * This lives here rather than in `FormSubmitService.createErrorHandler`
   * because that handler closes over `file.name` but not the `File` — this loop
   * is the only place that still holds `file.size`.
   */
  private emitUploadError(response: ApiResponse, file: File): void {
    if (response.statusCode === HttpStatus.PayloadTooLarge) {
      this.resultSubject.next({
        kind: 'error',
        message: this.buildOversizeMessage(response, file),
      });
      return;
    }
    this.emitError(response, file.name);
  }

  /**
   * One sentence for BOTH 413 shapes.
   *
   * The backend can name the numbers when a file merely exceeds the per-file cap,
   * but NOT when the request body trips the ~52,000,000-byte transport limit — that
   * request is killed during model binding, before the action runs, so the server
   * never sees the file. Composing our own message from `limits()` and `file.size`
   * gives the user the same sentence either way.
   *
   * Unless we are GUESSING the cap. When `/limits` could not be fetched,
   * `FALLBACK_FILING_SESSION_LIMITS` is seated and quoting it could name a number the
   * server never enforced. In that case we defer to the server's own text, and — if it
   * sent none — fall back to a message that names no cap at all. Never compose a
   * sentence around a guessed limit.
   *
   * In practice `FormSubmitService.createErrorHandler` always supplies generic copy on a
   * 413 when the server sent none, so the cap-free branch is unreachable through the real
   * pipeline. It exists so this method is correct on its own terms rather than by relying
   * on a neighbouring service's behaviour.
   */
  private buildOversizeMessage(response: ApiResponse, file: File): string {
    if (!this.limitsService.isServerProvided()) {
      const serverErrors = normalizeApiErrors(response.errors);
      if (serverErrors.length > 0) {
        return serverErrors.join(' ');
      }
      return (
        `<b>${file.name}</b> is too large to upload. ` +
        `Please reduce the file size and try again.`
      );
    }

    const precision = environment.fileSizeDecimalPrecision;
    const cap = this.limitsService.limits().maxFileSizeBytes;

    return (
      `<b>${file.name}</b> exceeds the ${formatFileSize(cap, precision)} limit. ` +
      `Your file: ${formatFileSize(file.size, precision)}.`
    );
  }

  /**
   * Generic error path for every filing-session call.
   *
   * `errorCode` is consulted BEFORE the server's `errors` prose, because the
   * code is the stable contract and the prose is not: it varies with the
   * backend's `IncludeDiagnosticDetail` setting, and its Development form
   * previews the extracted document text — client financial data that must not
   * be rendered in a toast.
   *
   * One mapping serves both endpoints. The content codes normally arrive from
   * `POST .../files`; `POST .../submit` is a backstop that can also raise
   * `FS_FILE_MISSING`. Neither needs its own branch.
   */
  private emitError(response: ApiResponse, context: string): void {
    const fsMessage = resolveFsErrorMessage(response);
    if (fsMessage) {
      this.resultSubject.next({ kind: 'error', message: fsMessage });
      return;
    }

    const apiErrors = normalizeApiErrors(response.errors);
    const message =
      apiErrors.length > 0
        ? apiErrors.join(', ')
        : `Failed to convert <b>${context}</b>. Please try again later.`;
    this.resultSubject.next({ kind: 'error', message });
  }
}
